import shared from "../Shared/shared.module.css";
import {
  IconUser,
  IconInfoCircle,
  IconAlertCircle,
  IconWorld,
  IconBrandLinkedin,
  IconChevronsLeft,
  IconArrowRight,
} from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";
import { useContext, useEffect, useState } from "react";
import PhoneInput, { isValidPhoneNumber } from "react-phone-number-input";
import "react-phone-number-input/style.css";
import { talentPersonalInfo, talentProfile } from "../../../services/talentCalls";
import { UserContext } from "../../../context";
import { errorHandler } from "../../../utils";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { countWords, locationSelectStyle } from "../../../utils/functions";
import GooglePlacesAutocomplete from "react-google-places-autocomplete";
import FullScreenLoader from "../../../components/Loaders/FullScreen/loader";

const WORK_MODES = ["Open to relocate", "Work Remotely", "Onsite"];
const GENDERS = ["Male", "Female"];

const Field = ({ label, required, error, children }) => (
  <div className={shared.formField}>
    <label className={shared.formLabel}>
      {label} {required && <span className={shared.req}>*</span>}
    </label>
    {children}
    {error && (
      <div className={shared.fieldErr}>
        <IconAlertCircle size={13} /> {error}
      </div>
    )}
  </div>
);

const Stage2 = () => {
  const { data, setData, isDev, GOOGLE_API_KEY } = useContext(UserContext);
  const [locationValue, setLocationValue] = useState(null);
  const navigate = useNavigate();
  const [load, setLoad] = useState(false);
  const [payload, setPayload] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    dateOfBirth: "",
    gender: "",
    workMode: [],
    permanentAddress: "",
    proofOfAddress: undefined,
    proofOfIdentity: undefined,
    currentLocation: "",
    webSiteUrl: "",
    linkedInUrl: "",
    summary: "",
  });
  const [errors, setErrors] = useState({});
  const [viewFileNames, setViewFileNames] = useState({ proofOfAddress: "", proofOfIdentity: "" });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const getUserProfile = async () => {
    try {
      const res = await talentProfile();
      if (res?.data?.data?.talent) setData(res?.data?.data?.talent);
    } catch (_) {}
  };

  useEffect(() => {
    getUserProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const extractNameParts = (fullName) => {
    if (!fullName) return { firstName: "", lastName: "" };
    const names = fullName.trim().split(/\s+/);
    if (names.length === 1) return { firstName: names[0], lastName: "" };
    return { firstName: names[0], lastName: names.slice(1).join(" ") };
  };

  useEffect(() => {
    const extractedDataString = localStorage.getItem("extractedResumeData");
    const extractedData = extractedDataString ? JSON.parse(extractedDataString) : null;
    const nameParts = extractNameParts(extractedData?.fullName);
    const profileData = data || {};

    let formattedPhone = extractedData?.phone || profileData?.phone || "";
    if (formattedPhone && !formattedPhone.startsWith("+")) formattedPhone = `+${formattedPhone}`;

    setPayload((prev) => ({
      ...prev,
      firstName: nameParts.firstName || profileData?.firstName || "",
      lastName: nameParts.lastName || profileData?.lastName || "",
      email: profileData?.email || extractedData?.email || "",
      phone: formattedPhone,
      currentLocation: profileData?.currentLocation || "",
      dateOfBirth: profileData?.dateOfBirth ? profileData?.dateOfBirth.substring(0, 10) : "",
      permanentAddress: profileData?.permanentAddress || "",
      gender: profileData?.gender || "",
      workMode: profileData?.workMode || [],
      webSiteUrl: profileData?.webSiteUrl || "",
      summary: profileData?.summary || "",
      linkedInUrl: profileData?.linkedInUrl || "",
    }));

    if (profileData?.proofOfAddress || profileData?.proofOfIdentity) {
      setViewFileNames({ proofOfIdentity: profileData?.proofOfIdentity || "", proofOfAddress: profileData?.proofOfAddress || "" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const validateField = (value, field) => {
    const newErrors = { ...errors };
    if (field === "dateOfBirth" && value > new Date().toISOString().split("T")[0]) {
      newErrors[field] = "Date of birth cannot be in the future";
    }
    if (field === "phone") {
      if (!value) newErrors[field] = "This field is required";
      else if (!isValidPhoneNumber(value)) newErrors[field] = "Please enter a valid phone number";
      else delete newErrors[field];
    }
    if (field === "email" && value) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(value)) newErrors[field] = "Please enter a valid email address";
      else delete newErrors[field];
    }
    if (field === "proofOfAddress") {
      if (!value && !viewFileNames.proofOfAddress) {
        // optional for now
      } else delete newErrors[field];
    } else if (field !== "webSiteUrl" && field !== "summary" && field !== "proofOfIdentity" && field !== "linkedInUrl" && (value === undefined || value === "")) {
      newErrors[field] = "This field is required";
    } else {
      delete newErrors[field];
    }
    setErrors(newErrors);
  };

  const handleChange = (e, field) => {
    setPayload((p) => ({ ...p, [field]: e.target.value }));
    validateField(e.target.value, field);
  };

  const handleDescriptionChange = (e) => {
    const { value } = e.target;
    if (countWords(value) <= 250) handleChange(e, "summary");
  };

  const handleDateChange = (date) => {
    setErrors((prev) => ({ ...prev, dateOfBirth: undefined }));
    setPayload((p) => ({ ...p, dateOfBirth: date ? date.toISOString() : null }));
  };

  const handleCheckbox = (e) => {
    const newArray = [...payload.workMode];
    if (e.target.checked) newArray.push(e.target.value);
    else {
      const idx = newArray.indexOf(e.target.value);
      if (idx > -1) newArray.splice(idx, 1);
    }
    setPayload((p) => ({ ...p, workMode: newArray }));
  };

  const handleChangeLocation = (selectedOption) => {
    setPayload((p) => ({ ...p, currentLocation: selectedOption.label }));
    setLocationValue(selectedOption);
  };

  const validatePayload = () => {
    const today = new Date().toISOString().split("T")[0];
    const newErrors = {};
    if (payload?.dateOfBirth > today) newErrors.dateOfBirth = "Date of birth cannot be in the future";
    if (payload?.email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(payload.email)) newErrors.email = "Please enter a valid email address";
    }
    if (!payload?.phone) newErrors.phone = "This field is required";
    else if (!isValidPhoneNumber(payload?.phone)) newErrors.phone = "Please enter a valid phone number";

    for (const key in payload) {
      if (["webSiteUrl", "summary", "proofOfIdentity", "linkedInUrl"].includes(key)) continue;
      if (key === "proofOfAddress") continue;
      if (payload[key] === undefined || payload[key] === "") newErrors[key] = "This field is required";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!data) return;
    if (!validatePayload()) return;
    try {
      setLoad(true);
      setData((prev) => ({ ...prev, ...payload }));
      const res = await talentPersonalInfo(payload);
      setLoad(false);
      if (res?.status === 200) navigate("?stage=3");
    } catch (error) {
      setLoad(false);
      error?.response?.data
        ? errorHandler(error?.response?.data?.errors, "Error")
        : errorHandler("Network error: Something went wrong, Please check your internet connection and try again.", "Error");
    }
  };

  return (
    <form onSubmit={submit}>
      {load && <FullScreenLoader />}
      <div className={shared.panelHero}>
        <div className={shared.panelHeroLeft}>
          <div className={shared.panelHeroIcon}>
            <IconUser size={22} />
          </div>
          <div>
            <div className={shared.panelTitle}>Personal Information</div>
            <div className={shared.panelSubtitle}>Please provide us with your personal details</div>
          </div>
        </div>
      </div>

      <div className={shared.panelBody}>
        <div className={shared.cautionNote}>
          <IconInfoCircle size={15} />
          <span>We've pre-filled some information from your resume. Please review everything for accuracy.</span>
        </div>

        <div className={shared.formRow}>
          <Field label="First Name" required error={errors?.firstName}>
            <input
              className={shared.formInput}
              type="text"
              placeholder="Jane"
              value={payload.firstName}
              onChange={(e) => {
                const v = e.target.value;
                if (v === "" || /^[A-Za-z]+(?: [A-Za-z]+)*$/.test(v)) handleChange(e, "firstName");
              }}
            />
          </Field>
          <Field label="Last Name" required error={errors?.lastName}>
            <input
              className={shared.formInput}
              type="text"
              placeholder="Doe"
              value={payload.lastName}
              onChange={(e) => {
                const v = e.target.value;
                if (v === "" || /^[A-Za-z]+(?: [A-Za-z]+)*$/.test(v)) handleChange(e, "lastName");
              }}
            />
          </Field>
        </div>

        <div className={shared.formRow}>
          <Field label="Email Address" required error={errors?.email}>
            <input
              className={shared.formInput}
              type="email"
              placeholder="janedoe@awasource.com"
              value={payload.email}
              onChange={(e) => handleChange(e, "email")}
            />
          </Field>
          <Field label="Phone Number" required error={errors?.phone}>
            <PhoneInput
              international
              countryCallingCodeEditable={false}
              defaultCountry="NG"
              className={shared.formInput}
              style={{ height: 42, padding: "0 12px" }}
              value={payload.phone}
              onChange={(v) => {
                setPayload((p) => ({ ...p, phone: v }));
                validateField(v, "phone");
              }}
            />
          </Field>
        </div>

        <div className={shared.formRow}>
          <Field label="Date of Birth" required error={errors?.dateOfBirth}>
            <DatePicker
              selected={payload.dateOfBirth ? new Date(payload.dateOfBirth) : null}
              dateFormat="dd-MM-yyyy"
              placeholderText="dd-mm-yyyy"
              onChange={handleDateChange}
              className={shared.formInput}
              wrapperClassName="w-full"
              showMonthDropdown
              useShortMonthInDropdown
              showYearDropdown
              maxDate={(() => {
                const d = new Date();
                d.setFullYear(d.getFullYear() - 18);
                return d;
              })()}
            />
          </Field>
          <Field label="Current Location" required error={errors?.currentLocation}>
            <GooglePlacesAutocomplete
              apiKey={GOOGLE_API_KEY}
              selectProps={{
                styles: locationSelectStyle,
                name: "currentLocation",
                placeholder: payload.currentLocation || "Type in your location...",
                value: locationValue,
                onChange: handleChangeLocation,
                isSearchable: true,
                onKeyDown: (e) => {
                  if (e.key === "Enter") e.preventDefault();
                },
              }}
            />
          </Field>
        </div>

        <div className={`${shared.formRow} ${shared.formRowFull}`}>
          <Field label="Permanent Address" required error={errors?.permanentAddress}>
            <input
              className={shared.formInput}
              type="text"
              placeholder="Permanent Address"
              value={payload.permanentAddress}
              onChange={(e) => handleChange(e, "permanentAddress")}
            />
          </Field>
        </div>

        <div className={shared.formRow}>
          <Field label="Gender" required error={errors?.gender}>
            <div className={shared.choiceGroup}>
              {GENDERS.map((v) => (
                <label key={v} className={shared.radioRow}>
                  <input type="radio" name="gender" value={v} checked={payload.gender === v} onChange={(e) => handleChange(e, "gender")} /> {v}
                </label>
              ))}
            </div>
          </Field>
          <Field label="Work Mode" required error={errors?.workMode}>
            <div className={`${shared.choiceGroup} ${shared.choiceGroupWrap}`}>
              <div className={shared.checkboxInlineGroup}>
                {WORK_MODES.map((v) => (
                  <label key={v} className={shared.checkboxRow}>
                    <input type="checkbox" name="workMode" value={v} checked={payload.workMode.includes(v)} onChange={handleCheckbox} /> {v}
                  </label>
                ))}
              </div>
            </div>
          </Field>
        </div>

        <div className={shared.formRow}>
          <Field label="Website Url" error={errors?.webSiteUrl}>
            <div style={{ position: "relative" }}>
              <IconWorld size={16} style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "var(--text-muted)" }} />
              <input
                className={shared.formInput}
                style={{ paddingLeft: 36 }}
                type="text"
                placeholder="yourportfolio.com"
                value={payload.webSiteUrl}
                onChange={(e) => handleChange(e, "webSiteUrl")}
              />
            </div>
          </Field>
          <Field label="LinkedIn Url" error={errors?.linkedInUrl}>
            <div style={{ position: "relative" }}>
              <IconBrandLinkedin size={16} style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "#0a66c2" }} />
              <input
                className={shared.formInput}
                style={{ paddingLeft: 36 }}
                type="text"
                placeholder="https://linkedin.com/in/yourname"
                value={payload.linkedInUrl}
                onChange={(e) => handleChange(e, "linkedInUrl")}
              />
            </div>
          </Field>
        </div>

        <div className={`${shared.formRow} ${shared.formRowFull}`}>
          <Field
            label={
              <>
                Summary <span style={{ fontSize: 11, color: "var(--text-muted)", fontWeight: 500 }}>({countWords(payload.summary ?? "")}/250 words)</span>
              </>
            }
            error={errors?.summary}
          >
            <textarea
              className={shared.formTextarea}
              placeholder="Tell us about yourself"
              value={payload.summary}
              onChange={handleDescriptionChange}
            />
          </Field>
        </div>
      </div>

      <div className={shared.panelFooter}>
        <button type="button" className={shared.btnBack} onClick={() => navigate("?stage=1")}>
          <IconChevronsLeft size={16} /> Back
        </button>
        <div className={shared.footerActions}>
          <button type="submit" className={shared.btnPrimary} disabled={load}>
            Next <IconArrowRight size={15} />
          </button>
        </div>
      </div>
    </form>
  );
};

export default Stage2;
